import Sortable from 'sortablejs'
import { Controller } from '@hotwired/stimulus'

// WARNING : we had issues with stimulus-sortable when having nested sortable elements.
// So we switched to sortablejs directly.
// At some point we should try to update stimulus-sortable to the latest version and see if we can use it again.
// Maybe we can stay with vanilla sortablejs instead, as the wrapper is very lightweight anyway

export default class extends Controller {
  static values = {
    chosenClass: { type: String, default: 'bg-white' },
    handle: { type: String, default: '' }
  }

  static SCROLL_SENSITIVITY = 120
  static SCROLL_MAX_SPEED = 20

  connect () {
    this._scrollContainer = this.#findScrollContainer()
    this._pointerY = null
    this._rafId = null

    this._trackPointer = (e) => {
      this._pointerY = e.touches ? e.touches[0].clientY : e.clientY
    }

    this.sortable = Sortable.create(this.element, {
      onStart: () => this.#startScroll(),
      onEnd: () => {
        this.#stopScroll()
        this.element.dispatchEvent(new CustomEvent('sorted', { bubbles: true }))
      },
      chosenClass: this.chosenClassValue,
      animation: 150,
      ...(this.handleValue && { handle: this.handleValue })
    })
  }

  disconnect () {
    this.#stopScroll()
    this.sortable?.destroy()
  }

  #startScroll () {
    document.addEventListener('pointermove', this._trackPointer)
    document.addEventListener('touchmove', this._trackPointer, { passive: true })
    this._rafId = window.requestAnimationFrame(() => this.#scrollLoop())
  }

  #stopScroll () {
    window.cancelAnimationFrame(this._rafId)
    this._rafId = null
    this._pointerY = null
    document.removeEventListener('pointermove', this._trackPointer)
    document.removeEventListener('touchmove', this._trackPointer)
  }

  #scrollLoop () {
    if (this._pointerY !== null) {
      const { top, bottom } = this._scrollContainer.getBounds()
      const { SCROLL_SENSITIVITY: sensitivity, SCROLL_MAX_SPEED: maxSpeed } = this.constructor

      const distFromTop = this._pointerY - top
      const distFromBottom = bottom - this._pointerY

      if (distFromTop < sensitivity && distFromTop > 0) {
        this._scrollContainer.scrollBy(-maxSpeed * (1 - distFromTop / sensitivity))
      } else if (distFromBottom < sensitivity && distFromBottom > 0) {
        this._scrollContainer.scrollBy(maxSpeed * (1 - distFromBottom / sensitivity))
      }
    }

    this._rafId = window.requestAnimationFrame(() => this.#scrollLoop())
  }

  #findScrollContainer () {
    let el = this.element.parentElement
    while (el && el !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(el)
      if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
        return {
          getBounds: () => el.getBoundingClientRect(),
          scrollBy: (delta) => { el.scrollTop += delta }
        }
      }
      el = el.parentElement
    }
    return {
      getBounds: () => ({ top: 0, bottom: window.innerHeight }),
      scrollBy: (delta) => window.scrollBy(0, delta)
    }
  }
}
